import React, { FC } from 'react';
import { StringParam, useQueryParams } from 'use-query-params';
import { CheckboxField, Field } from '@atlaskit/form';
import Select, { AsyncSelect } from '@atlaskit/select';
import TextField from '@atlaskit/textfield';
import TextArea from '@atlaskit/textarea';
import { Checkbox } from '@atlaskit/checkbox';
import { useQuery } from '@tanstack/react-query';

import { SLOCrudPayload } from '@/ui';
import { formatOptionLabel, FormFieldTooltipLabel, HelpTooltip } from '@/components/primitive';
import {
  useAutoCompleteController,
  useErrorController,
  useFetchErrorController,
  useLocationController,
} from '@/controllers';
import { SloTypeFields, SloTypeFieldsWithoutCW } from '@/components/composed/forms/data-mapper';
import { MAX_TEXT_LENGTH } from '@/constants';
import { flexStart } from '@/styles';
import { apiResolve, apis } from '@/services';
import TagsInput, { TagsValue } from '@/components/composed/forms/slo/fields/tags-input';
import { validUrl } from '@/utils';
import {
  ConditionalErrorMessage,
  VALUE_IS_INVALID,
  VALUE_IS_REQUIRED,
} from '@/components/composed/forms/form-errors';

import DraftStatus from '../../slo-card/lozenges/draft-status-lozenge';
import { helperCopy } from '../copy';

import SliFormFields from './fields';
import AlertingOptions from './fields/alerting-options';

const validateName = (value?: string) => {
  if (!value) {
    return VALUE_IS_REQUIRED;
  }

  return undefined;
};
const validateCapability = (value?: FieldOption<string>) => (value ? undefined : VALUE_IS_REQUIRED);

const validateRunbookUrl = (value?: string) =>
  !value || validUrl(value) ? undefined : VALUE_IS_INVALID;

type IProps = {
  lowTrafficThreshold?: number;
  suppressLowTrafficAlerts?: boolean;
  isUltraSteepBurnEnabled?: boolean;
  isLoading: boolean;
  initialCapability?: FieldOption<string>;
} & Partial<SLOCrudPayload>;

const SloForm: FC<IProps> = ({
  name = '',
  description = '',
  runbookUrl = '',
  lowTrafficThreshold,
  suppressLowTrafficAlerts,
  isUltraSteepBurnEnabled,
  isLoading,
  capability,
  type = SloTypeFields.RELIABILITY,
  objective = 99.999, // to show users that we support 5 9s
  goodQuery = '',
  badQuery = '',
  uiMetrics,
  isDraft = true,
  initialCapability,
  manualTag = {},
  serviceId = null,
  serviceIdCW = '',
  namespace = '',
  environment = '',
  rollupType = '',
  resourceName = '',
  successExpression = '',
  failureExpression = '',
}) => {
  const { isSLOGroupMemberPage } = useLocationController();
  const isDisabled = isLoading || isSLOGroupMemberPage;

  // This flag is archived in LD but we might enable the CloudWatch SLO in the
  // future in statsig so we keep it
  const allowCloudWatchSlo = false;
  const [localSloType, setLocalSLOType] = React.useState(type);

  const loadCapabilities = useAutoCompleteController('capability');

  React.useEffect(() => {
    setLocalSLOType(type);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const localSloTypeFields = Object.values(
    allowCloudWatchSlo ? SloTypeFields : SloTypeFieldsWithoutCW
  );
  const isLocalSloTypeCW =
    localSloType === SloTypeFields.RELIABILITY_CLOUDWATCH ||
    localSloType === SloTypeFields.PERFORMANCE_CLOUDWATCH;

  const searchServiceIds = useAutoCompleteController('service', { formatPlainOption: true });

  return (
    <>
      <Field
        name="name"
        label={<FormFieldTooltipLabel isRequired label="Name" content={helperCopy.slo.name} />}
        isRequired
        validate={validateName}
        defaultValue={name}
        isDisabled={isDisabled}
      >
        {({ fieldProps, error }) => (
          <>
            <TextField
              maxLength={MAX_TEXT_LENGTH}
              placeholder="e.g. Name of the SLO.."
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...fieldProps}
            />
            <ConditionalErrorMessage
              error={error}
              requiredErrorString="Don't forget to name your SLO."
            />
          </>
        )}
      </Field>
      <Field<FieldOption<string>>
        name="capability"
        label="Capability"
        isRequired
        isDisabled={isDisabled}
        defaultValue={capability || initialCapability}
        validate={validateCapability}
      >
        {({ fieldProps: { id, ...rest }, error }) => (
          <>
            <AsyncSelect
              placeholder="e.g. A previously created capability"
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...rest}
              inputId={id}
              defaultOptions
              formatOptionLabel={formatOptionLabel}
              loadOptions={loadCapabilities}
              validationState={error ? 'error' : 'default'} // https://product-fabric.atlassian.net/browse/DSP-821
            />
            <ConditionalErrorMessage error={error} requiredErrorString="Select a capability" />
          </>
        )}
      </Field>
      <Field<FieldOption<API.SLOTypeName>>
        name="type"
        label={
          <FormFieldTooltipLabel isRequired label="SLO Type" content={helperCopy.slo.sloType} />
        }
        isRequired
        isDisabled={isDisabled}
        defaultValue={type}
      >
        {({ fieldProps: { id, onChange, ...rest }, error }) => (
          <>
            <Select
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...rest}
              inputId={id}
              options={localSloTypeFields}
              onChange={(args: FieldOption<API.SLOTypeName>) => {
                if (!args) return;
                onChange(args);
                setLocalSLOType(args);
              }}
              validationState={error ? 'error' : 'default'} // https://product-fabric.atlassian.net/browse/DSP-821
            />
            <ConditionalErrorMessage error={error} />
          </>
        )}
      </Field>
      <Field<string, HTMLTextAreaElement>
        name="description"
        label="Description"
        defaultValue={description}
        isDisabled={isDisabled}
      >
        {({ fieldProps: { ...rest }, error }) => (
          <>
            {/* @ts-ignore */}
            <TextArea
              minimumRows={1}
              placeholder="This measures.."
              {...rest}
              maxLength={MAX_TEXT_LENGTH}
            />
            <ConditionalErrorMessage error={error} />
          </>
        )}
      </Field>
      <Field
        name="runbookUrl"
        label={
          <FormFieldTooltipLabel
            isRequired={false}
            label="Runbook URL"
            content={helperCopy.slo.runbookUrl}
          />
        }
        defaultValue={runbookUrl}
        isDisabled={isDisabled}
        validate={validateRunbookUrl}
      >
        {({ fieldProps, error }) => (
          <>
            <TextField
              maxLength={MAX_TEXT_LENGTH}
              placeholder="e.g. http://hello.atlassian.net/wiki/spaces/abcdefg"
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...fieldProps}
            />
            <ConditionalErrorMessage error={error} invalidErrorString="Please enter a valid URL." />
          </>
        )}
      </Field>
      <SliFormFields
        goodQuery={goodQuery}
        badQuery={badQuery}
        objective={objective}
        isDisabled={isDisabled}
        type={localSloType}
        uiMetrics={uiMetrics}
        serviceId={serviceIdCW}
        namespace={namespace}
        environment={environment}
        rollupType={rollupType}
        resourceName={resourceName}
        successExpression={successExpression}
        failureExpression={failureExpression}
      />
      <AlertingOptions
        lowTrafficThreshold={lowTrafficThreshold}
        suppressLowTrafficAlerts={suppressLowTrafficAlerts}
        isUltraSteepBurnEnabled={isUltraSteepBurnEnabled}
        isDisabled={isDisabled}
      />
      <Field<TagsValue>
        name="manualTag"
        label={<FormFieldTooltipLabel label="Tags" content={helperCopy.slo.tags} />}
        isDisabled={isDisabled}
        defaultValue={manualTag}
      >
        {({ fieldProps: { isInvalid, value, onChange }, error }) => (
          <>
            <TagsInput
              value={value}
              onChange={onChange}
              isInvalid={isInvalid}
              isDisabled={isDisabled}
            />
            <ConditionalErrorMessage error={error} />
          </>
        )}
      </Field>
      {isLocalSloTypeCW ? null : (
        <Field<any>
          name="serviceId"
          label={
            <FormFieldTooltipLabel
              label="Service Id (SLIME Migration)"
              content={helperCopy.slo.serviceId}
            />
          }
          isDisabled={isDisabled}
          defaultValue={serviceId}
        >
          {({ fieldProps: { id, ...rest }, error }) => (
            <>
              <AsyncSelect
                placeholder="Select a service.."
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...rest}
                inputId={id}
                defaultOptions
                loadOptions={searchServiceIds}
                validationState={error ? 'error' : 'default'} // https://product-fabric.atlassian.net/browse/DSP-821
              />
              <ConditionalErrorMessage error={error} />
            </>
          )}
        </Field>
      )}
      <CheckboxField name="isDraft" defaultIsChecked={isDraft} isDisabled={isDisabled}>
        {({ fieldProps, error }) => (
          <>
            <span css={flexStart}>
              <Checkbox
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...fieldProps}
                label={
                  <>
                    Save this as <DraftStatus />
                  </>
                }
              />
              <HelpTooltip>
                <div>
                  <h5>What is a Draft SLO?</h5>
                  <p>
                    By saving an SLO as <strong>Draft</strong>, your team is free to observe how the
                    SLO behaves in the wild before formally committing to it. This is ideal if you
                    are unsure about your SignalFlow queries.
                  </p>
                  <ul>
                    <li>You will not receive alerts for Draft SLOs.</li>
                    <li>Draft SLOs can be deleted at any time.</li>
                  </ul>
                </div>
              </HelpTooltip>
            </span>
            <ConditionalErrorMessage error={error} />
          </>
        )}
      </CheckboxField>
    </>
  );
};

const WithQueryParamsSloForm: React.FC<IProps> = props => {
  const [query] = useQueryParams({
    type: StringParam,
    experienceKey: StringParam,
  });

  const { type, experienceKey } = query;

  // PSREAU-1452 - the SLO form fetches options for  the experience and
  // populations selectors by calling the perfportal autocomplete endpoint as
  // the key is entered in the input box. When we read the experience key from
  // query params, this request is never fired and the associated populations
  // selector is empty. So we manually make the request with the query param
  // here and pass the experience and populations info from here to the form.

  const { onError } = useFetchErrorController();
  const setGlobalError = useErrorController();

  const { isSuccess, data: experiencePopulations } = useQuery(
    ['perfportal', 'autocomplete', experienceKey],
    () => apiResolve(apis.ufoResource.autocompleteExperience({ name: experienceKey! })),
    {
      onError,
      enabled: !!experienceKey,
      // Set cache time of 1 minute for the autocomplete results of a specific experience key,
      // unlikely to be reused.
      cacheTime: 1 * 60 * 1000,
    }
  );

  const { uiMetrics = {} } = props;
  if (experienceKey && isSuccess && experiencePopulations) {
    const selected = experiencePopulations!.find(ex => ex.name === experienceKey);
    if (selected) {
      uiMetrics.experience = Object.assign(uiMetrics?.experience ?? {}, {
        value: selected.name,
        label: selected.name,
        populations: selected.populations,
      });
    } else {
      setGlobalError({
        title: 'Error prefilling SLO',
        message: `No experience matching key ${experienceKey} could be found`,
        skipAnalysis: true,
      });
    }
  }

  const wrappedProps = {
    ...props,
    ...(type === 'UI_PERFORMANCE' && { type: SloTypeFields.UI_PERFORMANCE }),
    uiMetrics,
  };

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <SloForm {...wrappedProps} />;
};

export default WithQueryParamsSloForm;
